const DEFAULT_LITELLM_PROXY_URL = "http://localhost:4000/api";
const DEFAULT_LITELLM_SERVICE_TOKEN = "sk-local";

export const litellmProxyUrl = process.env.LITELLM_PROXY_URL || DEFAULT_LITELLM_PROXY_URL;
export const litellmServiceToken = process.env.LITELLM_SERVICE_TOKEN || DEFAULT_LITELLM_SERVICE_TOKEN;

let cachedIdToken: { token: string; expiresAt: number } | null = null;

function getAudienceFromUrl(url: string): string {
  return url.replace(/\/api\/?$/, "");
}

async function getGoogleIdToken(audience: string): Promise<string | null> {
  if (process.env.NODE_ENV !== "production") {
    return null;
  }

  const now = Date.now();
  if (cachedIdToken && cachedIdToken.expiresAt > now) {
    return cachedIdToken.token;
  }

  try {
    const metadataUrl = `http://metadata.google.internal/computeMetadata/v1/instance/service-accounts/default/identity?audience=${audience}`;
    const response = await fetch(metadataUrl, {
      headers: { "Metadata-Flavor": "Google" },
    });

    if (!response.ok) {
      return null;
    }

    const token = await response.text();
    cachedIdToken = {
      token,
      expiresAt: now + 55 * 60 * 1000,
    };

    return token;
  } catch {
    return null;
  }
}

interface LitellmHeaderOptions {
  audienceUrl?: string;
  includeServiceToken?: boolean;
}

export async function createLitellmHeaders(
  headersInit?: HeadersInit,
  options: LitellmHeaderOptions = {},
): Promise<Headers> {
  const headers = new Headers(headersInit);
  const includeServiceToken = options.includeServiceToken ?? true;

  if (includeServiceToken && !headers.has("Authorization")) {
    headers.set("Authorization", `Bearer ${litellmServiceToken}`);
  }

  const audience = getAudienceFromUrl(options.audienceUrl || litellmProxyUrl);
  const idToken = await getGoogleIdToken(audience);
  if (idToken) {
    headers.set("X-Serverless-Authorization", `Bearer ${idToken}`);
  }

  return headers;
}

export async function litellmFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  const headers = await createLitellmHeaders(init?.headers);
  return fetch(input, { ...init, headers });
}
